import type * as React from "react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Small "BETA" / "v1" / status pill rendered next to the title. */
  badge?: React.ReactNode;
  /** Tiny breadcrumb-style trail rendered above the title. */
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
  /** Pill-tab strip rendered below the title row. */
  tabs?: React.ReactNode;
  className?: string;
}

/**
 * Standard page header for every workspace route.
 *
 * Layout: [eyebrow] / [title + badge] over [subtitle], with [actions]
 * pinned right and an optional [tabs] strip beneath. Built from
 * shadcn primitives so dark / light themes inherit the right surfaces
 * automatically.
 */
export function PageHeader({
  title,
  subtitle,
  badge,
  eyebrow,
  actions,
  tabs,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn("mb-3 space-y-2", className)}>
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0 space-y-1">
          {eyebrow ? (
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {eyebrow}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold tracking-tight text-foreground">{title}</h1>
            {badge ? (
              typeof badge === "string" ? (
                <Badge variant="lemon" className="h-4 px-1.5 text-[9px] uppercase tracking-[0.1em]">
                  {badge}
                </Badge>
              ) : (
                badge
              )
            ) : null}
          </div>
          {subtitle ? (
            <p className="max-w-2xl text-xs leading-relaxed text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">{actions}</div>
        ) : null}
      </div>
      {tabs ? <div className="pt-0.5">{tabs}</div> : null}
    </header>
  );
}
